<template>
  <div class="w-full h-full flex-col min-h-[350px]">
    <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute rounded-t-lg left-0 top-0 pointer-events-none" />
    <div class="flex items-center w-full">
      <Tag class="w-10 h-10 -ml-2" color="#000" />
      <span class="mt-1 ml-2 text-3xl font-bold text-black">{{ $t("Tags") }}</span>
      <div
        class="flex items-center justify-center w-8 h-8 ml-auto mr-3 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
        @click="$emit('close')">
        <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" /><span
          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
          {{ $t("Close") }}</span>
      </div>
    </div>
    <div class="text-common-grey">
      {{ $t("Search and add tags to your activities. Use them to filter, search and manage your activities.") }}
    </div>
    <div class="mt-16">
      <div class="flex flex-row items-center w-full gap-2">
        <TextInput
          v-model="tag_name"
          :only-alpha-numeric="false"
          :value="tag_name"
          class="w-[270px] max-w-[270px]"
          :is-box-background="true"
          :show-label="false"
          :auto-focus="false"
          input-classes="appearance-none block px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light bg-white focus:border-blue-light sm:text-sm"
          name="tag_name"
          type="text"
          :placeholder="`${$store.state.data.actions_tags?.length ? $t('Search or create tag') : $t('Name your tag')}`"
          label=""
          @input="tag_name = $event.target.value" />
        <div v-if="tag_name" class="h-10 w-10 !p-0 flex items-center rounded-md border-none">
          <TextInput
            :value="new_tag.color"
            label=""
            classes="h-10 w-10 !p-0 flex items-center rounded-md border-none"
            input-classes="h-10 w-10 overflow-hidden appearance-none rounded-md !p-0 border-none"
            name="color"
            type="color"
            :placeholder="$t('Tag Color')"
            @input="new_tag.color = $event.target.value" />
        </div>
        <button
          v-if="tag_name"
          :disabled="is_name_duplicated"
          class="primary-btn w-fit"
          @click.prevent="tag_to_be_edited ? editTag() : createTag()">
          <span>{{ tag_to_be_edited ? $t("Edit tag") : $t("Create tag") }}</span>
          <Check class="w-6 h-6 ml-2" aria-hidden="true" />
        </button>
        <button
          v-if="tag_to_be_edited"
          type="button"
          class="w-fit cancel-btn py-2.5 -ml-1"
          @click="
            () => {
              tag_to_be_edited = null
              tag_name = ''
            }
          ">
          <span>{{ $t("Cancel") }}</span>
        </button>
      </div>
      <div v-if="$store.state.data.actions_tags?.length == 0" class="flex justify-center w-full my-2 mt-32 text-black">
        {{ $t("You currently have no tags created. Use the input box above to create your first tag.") }}
      </div>
      <div v-else class="flex flex-wrap gap-2 mt-4 max-h-[180px] overflow-y-auto">
        <div
          v-for="tag in all_tags"
          :key="tag?.id"
          class="flex focus:outline-none"
          :style="{ background: $store.state.data?.actions_tags.find((t) => t.id == tag)?.color }">
          <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
            <span
              class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
              :style="{
                background: $store.state.data?.actions_tags.find((t) => t.id == tag.id)?.color
              }">
              <Check class="block w-4 h-4 mr-2 text-white cursor-pointer group-hover:hidden" aria-hidden="true" />
              <X
                class="hidden w-4 h-4 mr-2 text-white cursor-pointer group-hover:block"
                aria-hidden="true"
                @click="(e) => handleSelectTag(false, tag)" />
              <span class="block text-white truncate">{{ tag.name }}</span>
            </span>
            <Pencil
              color="#899399"
              class="hidden w-5 h-5 ml-2 cursor-pointer group-hover:block"
              aria-hidden="true"
              @click="
                () => {
                  tag_to_be_edited = tag
                  tag_name = tag.name
                  new_tag.color = tag.color
                }
              " />
            <Trash2
              class="hidden w-6 h-6 ml-2 text-red-500 cursor-pointer group-hover:block"
              aria-hidden="true"
              @click="
                () => {
                  tag_to_be_deleted = tag
                  is_delete_tags_modal_open = true
                }
              " />
          </div>
          <div v-else class="flex items-center group">
            <span class="flex items-center rounded-md px-2 py-1.5 border border-gray-300">
              <div class="mr-3 w-3.5 h-3.5 rounded-sm" :style="{ background: tag.color }">
                <Check
                  class="w-3.5 h-3.5 text-white hidden group-hover:block cursor-pointer"
                  aria-hidden="true"
                  @click="(e) => handleSelectTag(true, tag)" />
              </div>
              <span class="block text-black truncate">{{ tag.name }}</span>
            </span>
            <Pencil
              color="#899399"
              class="hidden w-5 h-5 ml-2 cursor-pointer group-hover:block"
              aria-hidden="true"
              @click="
                () => {
                  tag_to_be_edited = tag
                  tag_name = tag.name
                  new_tag.color = tag.color
                }
              " />
            <Trash2
              class="hidden w-6 h-6 ml-2 text-red-500 cursor-pointer group-hover:block"
              aria-hidden="true"
              @click="
                () => {
                  tag_to_be_deleted = tag
                  is_delete_tags_modal_open = true
                }
              " />
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-center flex-1 w-full mt-4">
      <button class="px-3 cancel-btn w-fit" :class="[$store.state.data.actions_tags?.length == 0 ? '' : 'mt-28']" @click="$emit('close')">
        {{ $t("Close") }}
      </button>
    </div>
  </div>

  <!-- Delete Tags -->
  <Modal
    v-model:is-open="is_delete_tags_modal_open"
    max-width="max-w-[600px] mx-5"
    :use-toast="true"
    :default-success-msg="$t('Tag deleted successfully')">
    <template #form="{ close: closeModal, done: showIsDone }">
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Delete Tag") }}
      </h3>
      <div class="max-w-xl mt-2 text-sm text-gray-500">
        <p>{{ $t("Once you delete tag, you can't restore it.") }}</p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="confirmDeleteTag(showIsDone)">
          {{ $t("Delete Tag") }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
          @click="closeModal">
          {{ $t("Cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from "@/components/shared/Modal.vue"
import TextInput from "@/components/shared/TextInput.vue"
import { X, Check, Trash2, Pencil, Tag } from "lucide-vue-next"
import dataService from "@/services/data.service"
export default {
  components: {
    Tag,
    Modal,
    TextInput,
    Check,
    X,
    Trash2,
    Pencil
  },
  props: {
    selectedTags: {
      type: Array,
      required: false
    }
  },
  emits: ["changed", "close"],

  data() {
    return {
      tag_name: null,
      all_tags: this.$store.state.data.actions_tags,
      tag_to_be_deleted: null,
      tag_to_be_edited: null,
      new_tag: {
        name: "",
        object_type: null,
        is_public: "t",
        description: "",
        color: "#FF4646"
      },
      is_name_duplicated: null,
      is_delete_tags_modal_open: false,
      selected_tags: []
    }
  },
  watch: {
    tag_name() {
      this.is_name_duplicated = false
      if (!this.tag_to_be_edited) {
        let arr = []
        this.all_tags = this.$store.state.data?.actions_tags ? this.$store.state.data.actions_tags : []
        this.all_tags.map((t) => {
          if (t.name.toLowerCase() == this.tag_name.toLowerCase()) {
            this.is_name_duplicated = true
          }
          if (t.name.toLowerCase().includes(this.tag_name.toLowerCase())) {
            arr.push(t)
          }
        })
        this.all_tags = arr
      }
    }
  },

  created() {
    this.all_tags = this.$store.state.data.actions_tags
    this.all_tags.map((tag) => {
      if (this.selectedTags.find((t) => t.id == tag.id)) {
        this.selected_tags.push(tag)
      }
    })
  },

  methods: {
    createTag() {
      this.new_tag.name = this.tag_name
      this.new_tag.object_type = null
      dataService.createActionsTag(this.new_tag).then((result) => {
        result.data["applied_to"] = 0
        result.data["backup_applied_to"] = 0
        result.data["selected"] = null
        result.data["unselected"] = null
        result.data["undo"] = 0
        let tags = [result.data].concat(this.$store.state.data.actions_tags)
        this.$store.commit("data/setActionsTags", tags)
        this.all_tags = this.$store.state.data.actions_tags
        this.tag_name = ""
      })
    },
    editTag() {
      this.tag_to_be_edited.name = this.tag_name
      this.tag_to_be_edited.color = this.new_tag.color
      dataService.updateActionsTag(this.tag_to_be_edited.id, this.tag_to_be_edited).then(() => {
        let tags = this.$store.state.data.actions_tags
        let index = tags.findIndex((t) => t.id == this.tag_to_be_edited.id)
        tags[index].name = this.tag_to_be_edited.name
        tags[index].color = this.tag_to_be_edited.color
        this.$store.commit("data/setActionsTags", tags)
        this.tag_to_be_edited = null
        this.tag_name = ""
      })
    },
    confirmDeleteTag(showIsDone) {
      dataService.deleteActionsTag(this.tag_to_be_deleted.id).then(() => {
        let index = this.selected_tags.findIndex((r) => r.id === this.tag_to_be_deleted.id)
        if (index != -1) {
          this.selected_tags.splice(index, 1)
          this.$emit("changed", this.selected_tags)
        }
        let tags = this.$store.state.data.actions_tags.filter((tag) => tag.id != this.tag_to_be_deleted.id)
        this.all_tags = tags
        this.$store.commit("data/setActionsTags", tags)
        showIsDone()
      })
    },
    handleSelectTag(checked, tag) {
      if (checked) {
        this.selected_tags.push(tag)
      } else {
        let index = this.selected_tags.findIndex((r) => r.id === tag.id)
        this.selected_tags.splice(index, 1)
      }
      this.$emit("changed", this.selected_tags)
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
